import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { auth, getDocument, saveAutopilotPlanEdit } from "../../firebase";
import { buildFuturePlanEditPatch, formatJerusalemDateTimeLocal, getFuturePlanEditWarnings, getCurrentPlanScheduleWarnings, isStartedStep, parseJerusalemDateTimeLocal } from "../../Utils/autopilotAdmin";
import { getMissingEventDetailKeys, getStoredEventDetails } from "../../Utils/eventDetails";
import { callEventAutopilot } from "../../Utils/eventAutopilotApi";
import { getPhoneIdentityKey, normalizePhoneForStorage } from "../../Utils/phone";
import { isFreeContactDeletionLocked } from "../../Utils/freeRsvpVisibility";
import { getRecordUsage, recordOverageMessage } from "../../Utils/recordLimit";
import "./AutopilotSetup.css";
import EventDatePicker from "../EventDatePicker";
import EventTimePicker from "../EventTimePicker";
import ActiveAutopilotBanner from "../ActiveAutopilotBanner";
import CommercePackagePicker from "./CommercePackagePicker";
import PackageWorkflowCards from "./PackageWorkflowCards";
import PurchasedScheduleUnavailableWarning, { CUTOFF_WARNING_TEXT } from "./PurchasedScheduleUnavailableWarning";

const OUTDATED_DRAFT_MESSAGE = "חלק מהמועדים שבחרתם כבר חלפו. השינויים שלכם נשארו במסך ולא הוחלפו. יש לעדכן את המועדים שחלפו לפני ההפעלה.";
const INACTIVE_WARNING = "הטייס האוטומטי כבוי — לא יישלחו הודעות אוטומטיות לאורחים.";
const CUSTOM_VOICE_TEXT_LIMIT = 250;
const PERSONAL_RECORDING_LIMIT_SECONDS = 20;

const firstRealCount = (...values) => {
  const match = values.map(Number).find((value) => Number.isFinite(value) && value >= 0);
  return match === undefined ? null : match;
};

const completedStepSummary = (step) => {
  const result = step.executionResult || step.result || step.executionSummary || {};
  if (step.kind === "voice") {
    const operationCount = Object.keys(step.execution?.operations || {}).length;
    const calls = firstRealCount(result.callAttemptCount, operationCount || null, result.callsCompleted, result.completedCallCount, result.callCount, step.callsCompleted, step.callCount);
    const answered = firstRealCount(result.answeredCount, result.callsAnswered, step.answeredCount);
    const completedWorkflows = firstRealCount(result.completedWorkflowCount, result.sentCount);
    const responses = firstRealCount(result.responsesRecorded, result.responseCount, result.rsvpResponses, step.responsesRecorded, step.responseCount);
    const incomplete = firstRealCount(result.incompleteFailedCount, result.failedCount);
    const labels = [
      calls === null ? null : `בוצעו ${calls} ניסיונות חיוג`,
      answered === null ? null : `${answered} נענו`,
      completedWorkflows === null ? null : `${completedWorkflows} השלימו את תהליך המענה`,
      responses === null ? null : `${responses} תשובות אישור הגעה נרשמו`,
      incomplete === null || incomplete === 0 ? null : `${incomplete} שיחות לא הושלמו או נכשלו`,
    ].filter(Boolean);
    return labels.length ? labels : [String(result.summary || step.resultSummary || "החיוג הושלם")];
  }
  const confirmed = firstRealCount(result.sentCount, result.messagesSent, result.deliverySummary?.sentCount, step.deliverySummary?.sentCount, step.sentCount);
  // Submissions whose provider confirmation was lost count as sent for the customer; the split stays internal.
  const sent = confirmed === null ? null : confirmed + Number(result.unconfirmedCount || 0);
  return [sent === null ? String(result.summary || step.resultSummary || "השלב הושלם") : `נשלחו ${sent} הודעות`];
};

const enrichPlanWithVoiceHistory = (sourcePlan, calls = []) => {
  if (!sourcePlan?.steps || calls.length === 0) return sourcePlan;
  return {
    ...sourcePlan,
    steps: sourcePlan.steps.map((step) => {
      if (step.kind !== "voice") return step;
      const matching = calls.filter((call) => call.stepId === step.id
        && (!call.executionId || call.executionId === step.execution?.id));
      if (matching.length === 0) return step;
      const operations = { ...(step.execution?.operations || {}) };
      matching.forEach((call) => {
        if (call.operationId) operations[call.operationId] = { ...(operations[call.operationId] || {}), ...call };
      });
      return {
        ...step,
        execution: { ...step.execution, operations },
        executionResult: {
          ...(step.executionResult || {}),
          callAttemptCount: matching.length,
          answeredCount: matching.filter((call) => call.answered === true).length,
          completedWorkflowCount: matching.filter((call) => call.workflowCompleted === true).length,
          responsesRecorded: matching.filter((call) => call.responseRecorded === true).length,
          incompleteFailedCount: matching.filter((call) => call.workflowCompleted !== true).length,
        },
      };
    }),
  };
};

const formatAudioTime = (seconds) => {
  const safe = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
};

const CompactAudioPlayer = ({ src, disabled = false, onEnded, allowSeeking = true }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [src]);

  useEffect(() => {
    if (disabled && audioRef.current) audioRef.current.pause();
  }, [disabled]);

  if (!src) return null;
  return (
    <div className="autopilot-audio-player">
      <button type="button" disabled={disabled} onClick={() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) void audio.play().catch(() => setPlaying(false)); else audio.pause();
      }} aria-label={playing ? "השהיית הפתיח" : "השמע פתיח לטלפון"}>
        <span aria-hidden="true">{playing ? "❚❚" : "▶"}</span>
        <strong>{playing ? "השהיה" : "השמע פתיח לטלפון"}</strong>
      </button>
      <input
        type="range"
        min="0"
        max={duration || 0}
        step="0.1"
        value={Math.min(currentTime, duration || 0)}
        disabled={disabled || !allowSeeking}
        aria-label="התקדמות השמעת הפתיח"
        onChange={(event) => {
          const next = Number(event.target.value);
          if (audioRef.current) audioRef.current.currentTime = next;
          setCurrentTime(next);
        }}
      />
      <span className="autopilot-audio-player__time">{formatAudioTime(currentTime)} / {formatAudioTime(duration)}</span>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(event) => setDuration(Number(event.currentTarget.duration) || 0)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); onEnded?.(); }}
      />
    </div>
  );
};

const VoiceStagePreviewButton = ({ src, disabled = false }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    setPlaying(false);
    audioRef.current?.pause();
  }, [src]);

  useEffect(() => {
    if (disabled) audioRef.current?.pause();
  }, [disabled]);

  return (
    <span className="autopilot-step__voice-preview-control">
      <button
        type="button"
        className={`autopilot-step__voice-preview-button${playing ? " is-playing" : ""}`}
        disabled={disabled || !src}
        aria-label={playing ? "השהיית ההודעה שתתנגן" : "שמיעת ההודעה שתתנגן"}
        onClick={(event) => {
          event.stopPropagation();
          const audio = audioRef.current;
          if (!audio) return;
          if (!audio.paused) {
            audio.pause();
            return;
          }
          setPlaying(true);
          void audio.play().catch(() => setPlaying(false));
        }}
      >
        <span className="autopilot-step__voice-play-indicator" aria-hidden="true">{playing ? "◼" : "▶"}</span>
        <span>{playing ? "ההודעה מתנגנת" : "שמיעת ההודעה שתתנגן"}</span>
      </button>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </span>
  );
};

const errorMessage = (error) => ({
  AUTOPILOT_ACTIVATION_CUTOFF: CUTOFF_WARNING_TEXT,
  AUTOPILOT_RESUME_CONFLICT: "לא ניתן לבנות לוח זמנים תקין מהזמן הנוכחי. יש לתקן את מועדי השלבים.",
  AUTOPILOT_CONTACTS_REQUIRED: "יש להעלות לפחות איש קשר אחד לפני ההפעלה.",
  AUTOPILOT_RECORD_LIMIT_EXCEEDED: "קיימת חריגה ממכסת הרשומות. יש להסדיר אותה לפני הפעלת הטייס האוטומטי.",
  AUTOPILOT_ACTIVATION_STALE: "פרטי האירוע או החבילה השתנו מאז יצירת הטיוטה. יש לרענן וליצור לוח זמנים מעודכן.",
  AUTOPILOT_ALREADY_ACTIVATED: "הטייס האוטומטי כבר הופעל או שאינו במצב המאפשר הפעלה מחדש.",
  AUTOPILOT_COMPLETED_HISTORY_EXISTS: "לא ניתן להפעיל מחדש טיוטה כאשר קיימת היסטוריה של שלבים שהושלמו.",
  AUTOPILOT_DRAFT_INVALID: "לא ניתן להפעיל שליחת הודעות: חלק ממועדי השליחה אינם תקינים או אינם לפי סדר השלבים. יש לבדוק גם שלבים מושהים.",
  AUTOPILOT_PLAN_INVALID: "לוח הזמנים אינו תקין ולא ניתן לשמור אותו.",
  AUTOPILOT_STEP_STRUCTURE_IMMUTABLE: "לא ניתן להוסיף או להסיר שלבים מלוח הזמנים הזה.",
  AUTOPILOT_STEP_ID_DUPLICATE: "לוח הזמנים מכיל שלב כפול ולא ניתן לשמור אותו.",
  FUTURE_STEP_TIME_INVALID: "יש לקבוע לשלב המסומן תאריך ושעה עתידיים.",
  AUTOPILOT_STEP_CHANNEL_INVALID: "ערוץ השליחה של השלב המסומן אינו מתאים לסוג השלב.",
  AUTOPILOT_GIFT_AUDIENCE_INVALID: "קהל היעד של שלב המתנה אינו תקין.",
  AUTOPILOT_STEP_ORDER_INVALID: "מועד ההודעה המסומנת צריך להיות אחרי המועד של ההודעה הקודמת אליה. אנא שנו את השעה/תאריך.",
  AUTOPILOT_STEP_SPACING_INVALID: "יש לקבוע לפחות שעתיים בין שלב לשלב.",
  AUTOPILOT_STEP_EVENT_BOUNDARY_INVALID: "מועד השלב המסומן אינו מתאים למועד האירוע: שלב איסוף חייב להיות לפני האירוע והודעת תודה לאחריו.",
  AUTOPILOT_REMINDER_ORDER_INVALID: "שלב תזכורת לאורחים שלא ענו חייב להיות לאחר שלב הזמנה.",
  COMPLETED_STEP_IMMUTABLE: "לא ניתן לשנות שלב שכבר הושלם.",
  STARTED_STEP_IMMUTABLE: "אחד השלבים כבר התחיל להישלח ולא ניתן לשנותו. יש לרענן את העמוד ולנסות שוב.",
  AUTOPILOT_EXECUTION_ALREADY_STARTED: "לא ניתן לכבות את הטייס לאחר שהתחיל ניסיון שליחה או חיוג.",
  EVENT_SCHEDULE_DETAILS_REQUIRED: "יש להשלים ולשמור את תאריך ושעת האירוע.",
  PACKAGE_NOT_ASSIGNED: "לא הוגדרה חבילה לאירוע.",
  VOICE_EVENT_DETAILS_STALE: "פרטי האירוע השתנו. שמרו אותם ורעננו את מסך הטייס האוטומטי.",
  VOICE_INTRODUCTION_APPROVAL_REQUIRED: "יש לאשר את הפתיח לפני הפעלת הטייס האוטומטי.",
  VOICE_INTRODUCTION_APPROVAL_STALE: "הפתיח השתנה. יש להאזין לפתיח הנוכחי ולאשר אותו מחדש.",
})[error?.code || error?.message] || "הפעולה לא הושלמה. נסו שוב.";

const AutopilotSetup = ({ docID, visible, isReadOnly, addAlertText, refreshKey }) => {
  const [eventData, setEventData] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [lifecycle, setLifecycle] = useState({ status: "inactive" });
  const [plan, setPlan] = useState(null);
  const [draftSteps, setDraftSteps] = useState([]);
  const [activationPreview, setActivationPreview] = useState(null);
  const [activationPreviewLoading, setActivationPreviewLoading] = useState(false);
  const [activationPreviewError, setActivationPreviewError] = useState("");
  const [activationCutoffReached, setActivationCutoffReached] = useState(false);
  const [activationClock, setActivationClock] = useState(Date.now);
  const draftEditedRef = useRef({ eventId: docID, dirty: false });
  const [invalidStepIds, setInvalidStepIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [voiceState, setVoiceState] = useState({ loading: false, introduction: null, error: "" });
  const [voiceGenerationState, setVoiceGenerationState] = useState("idle");
  const [voiceApproval, setVoiceApproval] = useState(null);
  const [listenedVoiceFingerprint, setListenedVoiceFingerprint] = useState("");
  const [voiceMode, setVoiceMode] = useState("automatic");
  const [voicePanel, setVoicePanel] = useState("automatic");
  const [automaticVoiceText, setAutomaticVoiceText] = useState("");
  const [customText, setCustomText] = useState("");
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState("");
  const [recordedFileName, setRecordedFileName] = useState("event-owner-recording");
  const [recordedSource, setRecordedSource] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testCallState, setTestCallState] = useState({ loading: false, message: "", error: "" });
  const [editingStepId, setEditingStepId] = useState(null);
  const [voiceSettingsStepId, setVoiceSettingsStepId] = useState(null);
  const [voiceSectionOpen, setVoiceSectionOpen] = useState(true);
  const [pauseCandidate, setPauseCandidate] = useState(null);
  const voiceEnsureRequest = useRef(null);
  const voiceSettingsRef = useRef(null);
  const voicePreviewRequest = useRef(null);
  const editingStepOriginalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingCanvasRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const recordingLimitRef = useRef(null);
  const recordingFrameRef = useRef(null);
  const recordingAudioContextRef = useRef(null);
  const editTransitionTimerRef = useRef(null);
  const [closingStepId, setClosingStepId] = useState(null);

  const ownerCall = useCallback(async (action, payload = {}) => {
    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) throw Object.assign(new Error("Authentication required"), { code: "AUTOPILOT_AUTH_REQUIRED" });
    return callEventAutopilot({ idToken, eventId: docID, action, payload });
  }, [docID]);

  const load = useCallback(async () => {
    if (!docID) return;
    void refreshKey;
    setLoading(true);
    try {
      const [document, state, voiceHistory] = await Promise.all([
        getDocument({ docID }),
        ownerCall("inspect"),
        ownerCall("voiceHistory").catch(() => ({ calls: [] })),
      ]);
      const stored = document.data() || {};
      const enrichedPlan = enrichPlanWithVoiceHistory(state.plan || null, voiceHistory.calls || []);
      setEventData(stored);
      setAssignment(state.assignment || null);
      setLifecycle(state.lifecycle || { status: "inactive" });
      setPlan(enrichedPlan);
      setVoiceApproval(state.voiceIntroductionApproval || null);
      if (!(draftEditedRef.current.eventId === docID && draftEditedRef.current.dirty
        && (!state.lifecycle?.status || state.lifecycle.status === "inactive"))) {
        draftEditedRef.current = { eventId: docID, dirty: false };
        setDraftSteps(enrichedPlan?.steps ? JSON.parse(JSON.stringify(enrichedPlan.steps)) : []);
        setActivationPreview(null);
      }
      setActivationCutoffReached(false);
      setInvalidStepIds([]);
    } catch (error) {
      addAlertText({ mode: "danger", text: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [addAlertText, docID, ownerCall, refreshKey]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => {
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    clearInterval(recordingTimerRef.current);
    clearTimeout(recordingLimitRef.current);
    clearTimeout(editTransitionTimerRef.current);
    cancelAnimationFrame(recordingFrameRef.current);
    recordingAudioContextRef.current?.close?.().catch(() => {});
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
  }, [recordedUrl]);

  const eventDetails = useMemo(() => getStoredEventDetails(eventData || {}), [eventData]);
  const contacts = useMemo(() => Array.isArray(eventData?.contacts) ? eventData.contacts : [], [eventData?.contacts]);
  const usage = useMemo(() => getRecordUsage(eventData || {}, contacts), [contacts, eventData]);
  const assignedPackageHasVoice = assignment?.templateSnapshot?.steps?.some((step) => step?.kind === "voice") === true;
  const eligibleForSchedulePreview = Boolean(assignment && lifecycle.status === "inactive");
  const activationBlocked = activationCutoffReached || (() => {
    if (lifecycle.status !== "inactive") return false;
    try {
      return Date.parse(parseJerusalemDateTimeLocal(`${eventDetails.date}T${eventDetails.time}`)) - activationClock <= 3600000;
    } catch (_error) { return false; }
  })();
  useEffect(() => {
    if (!visible || lifecycle.status !== "inactive") return undefined;
    setActivationClock(Date.now());
    const timer = setInterval(() => setActivationClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [visible, lifecycle.status]);
  const hasTableNumberData = Array.isArray(eventData?.seatingTables) && eventData.seatingTables.length > 0;
  const currentVoiceFingerprint = String(voiceState.introduction?.fingerprint || "");
  const automaticVoiceApprovalRequired = lifecycle.status === "inactive"
    && assignedPackageHasVoice
    && voiceState.introduction?.mode === "automatic"
    && (!currentVoiceFingerprint || voiceApproval?.fingerprint !== currentVoiceFingerprint);

  useEffect(() => {
    if (automaticVoiceApprovalRequired && !voiceSectionOpen) setVoiceSectionOpen(true);
  }, [automaticVoiceApprovalRequired, voiceSectionOpen]);

  useEffect(() => {
    if (!assignment || !eventData || getMissingEventDetailKeys(eventDetails).length > 0
      || (lifecycle.status === "inactive" && !activationPreview)) return undefined;
    const requestKey = JSON.stringify({ packageId: assignment.packageId, event: eventDetails });
    if (!voicePreviewRequest.current || voicePreviewRequest.current.key !== requestKey) {
      voicePreviewRequest.current = {
        key: requestKey,
        promise: ownerCall("voicePreviewText", { mode: "automatic", language: "he" }),
      };
    }
    if (!voiceEnsureRequest.current || voiceEnsureRequest.current.key !== requestKey) {
      voiceEnsureRequest.current = {
        key: requestKey,
        promise: ownerCall("voiceGenerate", { mode: "automatic", event: { ...eventDetails, language: "he" }, ensureOnly: true }),
      };
    }
    let active = true;
    setVoiceState((current) => ({ ...current, loading: true, error: "" }));
    voicePreviewRequest.current.promise
      .then((result) => { if (active) setAutomaticVoiceText(result.text || ""); })
      .catch(() => { if (active) setAutomaticVoiceText(""); });
    voiceEnsureRequest.current.promise
      .then((result) => {
        if (active) {
          setVoiceState({ loading: false, introduction: result.introduction || null, error: "" });
          if (result.introduction?.mode === "automatic" && result.introduction.text) {
            setAutomaticVoiceText(result.introduction.text);
          }
        }
      })
      .catch((error) => {
        if (active) setVoiceState({ loading: false, introduction: null, error: errorMessage(error) });
      });
    return () => { active = false; };
  }, [activationPreview, assignment, eventData, eventDetails, lifecycle.status, ownerCall]);

  const displayPlan = useMemo(() => activationPreview
    ? { ...activationPreview, steps: draftSteps }
    : (plan ? { ...plan, steps: draftSteps } : null), [activationPreview, draftSteps, plan]);
  const scheduleWarnings = useMemo(() => getFuturePlanEditWarnings({ draftSteps }), [draftSteps]);
  const currentScheduleWarnings = useMemo(() => getCurrentPlanScheduleWarnings({ draftSteps }), [draftSteps]);
  const giftSchedulingBlocked = draftSteps.some(step => step.kind === "giftLink" && step.requiresAttentionReason === "GIFT_SCHEDULING_CUTOFF");
  const hasOutdatedDraftTimes = lifecycle.status === "inactive" && draftSteps.some(step =>
    step.executionStatus !== "paused" && Number.isFinite(Date.parse(step.scheduledAt)) && Date.parse(step.scheduledAt) <= activationClock);

  useEffect(() => {
    if (!visible || !eligibleForSchedulePreview || activationPreview) return undefined;
    let active = true;
    setActivationPreviewError("");
    setActivationCutoffReached(false);
    setActivationPreviewLoading(true);
    ownerCall("previewActivation")
      .then((result) => {
        if (active) {
          setActivationPreview(result.plan);
          setDraftSteps(JSON.parse(JSON.stringify(result.plan?.steps || [])));
          setInvalidStepIds([]);
        }
      })
      .catch((error) => {
        if (!active) return;
        if (error?.code === "AUTOPILOT_ACTIVATION_CUTOFF") {
          setActivationCutoffReached(true);
          setActivationPreviewError("");
          return;
        }
        setActivationPreviewError(errorMessage(error));
      })
      .finally(() => {
        if (active) setActivationPreviewLoading(false);
      });
    return () => { active = false; };
  }, [activationPreview, eligibleForSchedulePreview, ownerCall, visible]);

  const activate = async () => {
    if (!activationPreview || contacts.length === 0 || usage.overLimit || isReadOnly || activationBlocked) return;
    if (automaticVoiceApprovalRequired) {
      setVoiceSectionOpen(true);
      voiceSettingsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      voiceSettingsRef.current?.focus({ preventScroll: true });
      addAlertText({ mode: "danger", text: "יש לאשר את הפתיח לפני הפעלת הטייס האוטומטי." });
      return;
    }
    const now = new Date();
    const pastOrInvalid = draftSteps
      .filter((step) => !Number.isFinite(new Date(step.scheduledAt).getTime()) || new Date(step.scheduledAt) <= now)
      .map((step) => step.id);
    if (pastOrInvalid.length > 0) {
      setInvalidStepIds(pastOrInvalid);
      setActivationPreviewError(OUTDATED_DRAFT_MESSAGE);
      addAlertText({ mode: "danger", text: OUTDATED_DRAFT_MESSAGE });
      return;
    }
    setBusy(true);
    try {
      const validated = buildFuturePlanEditPatch({
        currentPlan: activationPreview,
        lifecycle: { status: "inactive", planRevision: 0 },
        draftSteps,
        now,
      });
      const draft = {
        ...validated.autopilotPlan,
        valid: true,
        lifecycle: { status: "inactive", activationTime: null },
        planRevision: 0,
      };
      const result = await ownerCall("activate", { plan: draft });
      draftEditedRef.current.dirty = false;
      setLifecycle(result.lifecycle);
      setPlan(result.plan);
      setDraftSteps(JSON.parse(JSON.stringify(result.plan.steps || [])));
      setActivationPreview(null);
      setInvalidStepIds([]);
      addAlertText({ mode: "success", text: "הטייס האוטומטי הופעל ולוח הזמנים נשמר." });
    } catch (error) {
      setInvalidStepIds(Array.isArray(error.details?.invalidStepIds) ? error.details.invalidStepIds : []);
      if (error?.code === "AUTOPILOT_ACTIVATION_CUTOFF") setActivationCutoffReached(true);
      // Keep the reviewed draft intact. A server rejection must not regenerate it.
      if (error?.code === "AUTOPILOT_DRAFT_INVALID") setActivationPreviewError(OUTDATED_DRAFT_MESSAGE);
      else if (error?.code === "AUTOPILOT_ACTIVATION_STALE") setActivationPreviewError(errorMessage(error));
      if (error?.code === "VOICE_INTRODUCTION_APPROVAL_REQUIRED") {
        setVoiceSectionOpen(true);
        voiceSettingsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        voiceSettingsRef.current?.focus({ preventScroll: true });
      }
      addAlertText({ mode: "danger", text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  const updateStep = (id, patch) => {
    draftEditedRef.current = { eventId: docID, dirty: true };
    setActivationPreviewError("");
    setInvalidStepIds((ids) => ids.filter((stepId) => stepId !== id));
    setDraftSteps((steps) => steps.map((step) => step.id === id ? { ...step, ...patch } : step));
  };

  const reactivateStep = (step) => {
    const scheduledAt = new Date(step.scheduledAt);
    if (!Number.isFinite(scheduledAt.getTime()) || scheduledAt <= new Date()) {
      setInvalidStepIds([step.id]);
      setEditingStepId(step.id);
      addAlertText({ mode: "danger", text: errorMessage({ code: "FUTURE_STEP_TIME_INVALID" }) });
      return;
    }
    updateStep(step.id, { executionStatus: "pending", requiresAttentionAt: null, requiresAttentionReason: null });
    setEditingStepId(step.id);
  };

  const toggleStepEditor = useCallback((stepId) => {
    if (closingStepId) return;
    if (!editingStepId) {
      const step = draftSteps.find((item) => item.id === stepId);
      editingStepOriginalRef.current = step ? JSON.parse(JSON.stringify(step)) : null;
      setEditingStepId(stepId);
      return;
    }
    setClosingStepId(editingStepId);
    clearTimeout(editTransitionTimerRef.current);
    editTransitionTimerRef.current = setTimeout(() => {
      setEditingStepId(editingStepId === stepId ? null : stepId);
      setClosingStepId(null);
      editingStepOriginalRef.current = null;
    }, 180);
  }, [closingStepId, draftSteps, editingStepId]);

  const cancelStepEditor = useCallback(() => {
    const originalStep = editingStepOriginalRef.current;
    if (editingStepId && originalStep) {
      setDraftSteps((steps) => steps.map((step) => step.id === editingStepId ? originalStep : step));
    }
    if (editingStepId) toggleStepEditor(editingStepId);
  }, [editingStepId, toggleStepEditor]);

  useEffect(() => {
    if (!editingStepId) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") cancelStepEditor();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [cancelStepEditor, editingStepId]);

  useEffect(() => {
    if (!voiceSettingsStepId) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setVoiceSettingsStepId(null);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [voiceSettingsStepId]);

  const saveTimeline = async () => {
    if (!plan || isReadOnly) return false;
    setBusy(true);
    try {
      const localPatch = buildFuturePlanEditPatch({ currentPlan: plan, lifecycle, draftSteps });
      const saved = await saveAutopilotPlanEdit({ docID, editSource: "customer", buildPatch: (latest) => buildFuturePlanEditPatch({ ...latest, draftSteps }) });
      const patch = saved?.patch || localPatch;
      draftEditedRef.current.dirty = false;
      setPlan(patch.autopilotPlan);
      setDraftSteps(JSON.parse(JSON.stringify(patch.autopilotPlan.steps)));
      setLifecycle(patch.autopilotLifecycle);
      setEventData((current) => ({ ...current, ...patch }));
      addAlertText({ mode: "success", text: "לוח הזמנים נשמר. לא נשלחו הודעות או שיחות." });
      return true;
    } catch (error) {
      setInvalidStepIds(Array.isArray(error.details?.invalidStepIds) ? error.details.invalidStepIds : []);
      addAlertText({ mode: "danger", text: errorMessage(error) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const generateVoice = async () => {
    setVoiceGenerationState("generating");
    setVoiceState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const result = await ownerCall("voiceGenerate", {
        mode: voiceMode,
        customText: voiceMode === "custom" ? customText : undefined,
        event: { ...eventDetails, language: "he" },
        ensureOnly: false,
      });
      setVoiceState({ loading: false, introduction: result.introduction, error: "" });
      if (voiceMode === "custom") setVoiceApproval(null);
      if (voiceMode === "automatic" && result.introduction?.text) setAutomaticVoiceText(result.introduction.text);
      setVoiceGenerationState("success");
      return true;
    } catch (error) {
      setVoiceState((current) => ({ ...current, loading: false, error: errorMessage(error) }));
      setVoiceGenerationState("idle");
      return false;
    }
  };

  const startEventTestCall = async () => {
    const identity = getPhoneIdentityKey(normalizePhoneForStorage(testPhone));
    if (!/^9725\d{8}$/.test(identity)) {
      setTestCallState({ loading: false, message: "", error: "יש להזין מספר טלפון ישראלי תקין, לדוגמה 0501234567." });
      return;
    }
    setTestCallState({ loading: true, message: "מחייג למספר שהוזן…", error: "" });
    try {
      await ownerCall("voiceTestCall", { phoneNumber: `+${identity}`, language: "he" });
      setTestCallState({ loading: false, message: "החיוג נשלח בהצלחה. השיחה תתקבל בעוד מספר רגעים.", error: "" });
    } catch (error) {
      const message = error?.code === "TEST_RECORDINGS_MISSING"
        ? "לא ניתן לבצע חיוג ניסיון עד שכל הקלטות המערכת הדרושות יהיו זמינות."
        : error?.code === "PHONE_INVALID"
          ? "יש להזין מספר טלפון ישראלי תקין, לדוגמה 0501234567."
          : "חיוג הניסיון לא הושלם. נסו שוב.";
      setTestCallState({ loading: false, message: "", error: message });
    }
  };

  const displayedVoiceText = voiceMode === "automatic"
    ? automaticVoiceText
    : (voiceState.introduction?.mode === "custom" ? voiceState.introduction.text : "");
  const recordedPanelOpen = voicePanel === "recording" || voicePanel === "upload";
  const displayedAudio = recordedPanelOpen
    ? (((voicePanel === "recording" && recordedSource === "recording") || (voicePanel === "upload" && recordedSource === "upload"))
      ? recordedUrl
      : (voiceState.introduction?.mode === "recorded" ? voiceState.introduction.playbackUrl : ""))
    : (voiceState.introduction?.mode === voiceMode ? voiceState.introduction.playbackUrl : "");

  const approveAutomaticVoice = async () => {
    if (!currentVoiceFingerprint || listenedVoiceFingerprint !== currentVoiceFingerprint) return;
    setBusy(true);
    try {
      const result = await ownerCall("voiceApprove", { fingerprint: currentVoiceFingerprint });
      setVoiceApproval(result.approval || null);
      setVoiceSectionOpen(false);
      addAlertText({ mode: "success", text: "הפתיח אושר. כעת ניתן להפעיל את הטייס האוטומטי." });
    } catch (error) {
      setListenedVoiceFingerprint("");
      addAlertText({ mode: "danger", text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"]
        .find((type) => window.MediaRecorder?.isTypeSupported?.(type));
      const recorder = preferred ? new MediaRecorder(stream, { mimeType: preferred }) : new MediaRecorder(stream);
      recordingChunksRef.current = [];
      recordingStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      setRecordingSeconds(0);
      const startedAt = Date.now();
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds(Math.min(PERSONAL_RECORDING_LIMIT_SECONDS, Math.floor((Date.now() - startedAt) / 1000)));
      }, 250);
      recordingLimitRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
      }, PERSONAL_RECORDING_LIMIT_SECONDS * 1000);

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext && recordingCanvasRef.current) {
        const audioContext = new AudioContext();
        recordingAudioContextRef.current = audioContext;
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        audioContext.createMediaStreamSource(stream).connect(analyser);
        const data = new Uint8Array(analyser.fftSize);
        const draw = () => {
          const canvas = recordingCanvasRef.current;
          if (!canvas || mediaRecorderRef.current?.state !== "recording") return;
          const context = canvas.getContext("2d");
          analyser.getByteTimeDomainData(data);
          context.clearRect(0, 0, canvas.width, canvas.height);
          context.strokeStyle = "#b23838";
          context.lineWidth = 2;
          context.beginPath();
          data.forEach((value, index) => {
            const x = (index / (data.length - 1)) * canvas.width;
            const y = (value / 255) * canvas.height;
            if (index === 0) context.moveTo(x, y); else context.lineTo(x, y);
          });
          context.stroke();
          recordingFrameRef.current = requestAnimationFrame(draw);
        };
        draw();
      }
      recorder.ondataavailable = (event) => { if (event.data?.size) recordingChunksRef.current.push(event.data); };
      recorder.onstop = () => {
        clearInterval(recordingTimerRef.current);
        clearTimeout(recordingLimitRef.current);
        cancelAnimationFrame(recordingFrameRef.current);
        recordingAudioContextRef.current?.close?.().catch(() => {});
        recordingAudioContextRef.current = null;
        const blob = new Blob(recordingChunksRef.current, { type: recorder.mimeType || preferred || "audio/webm" });
        setRecordedBlob(blob);
        setRecordedFileName("event-owner-recording.webm");
        setRecordedSource("recording");
        setRecordedUrl((current) => {
          if (current) URL.revokeObjectURL(current);
          return URL.createObjectURL(blob);
        });
        stream.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        setRecordingSeconds(Math.min(PERSONAL_RECORDING_LIMIT_SECONDS, Math.max(1, Math.round((Date.now() - startedAt) / 1000))));
        setRecording(false);
      };
      recorder.start(250);
      setRecording(true);
    } catch (_error) {
      setVoiceState((current) => ({ ...current, error: "לא ניתן לגשת למיקרופון. בדקו את הרשאת הדפדפן ונסו שוב." }));
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
  };

  const uploadVoiceRecording = async () => {
    if (!recordedBlob) return false;
    setVoiceState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const audioBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
        reader.onerror = reject;
        reader.readAsDataURL(recordedBlob);
      });
      const result = await ownerCall("voiceUpload", {
        language: "he", audioBase64, contentType: recordedBlob.type, originalName: recordedFileName,
      });
      setVoiceState({ loading: false, introduction: result.introduction, error: "" });
      setVoiceApproval(null);
      return true;
    } catch (error) {
      setVoiceState((current) => ({ ...current, loading: false, error: errorMessage(error) }));
      return false;
    }
  };

  const openVoiceSettings = (stepId) => {
    const savedIntroduction = voiceState.introduction;
    if (savedIntroduction?.mode === "custom") {
      setVoiceMode("custom");
      setVoicePanel("custom");
      setCustomText(savedIntroduction.text || "");
    } else if (savedIntroduction?.mode === "recorded") {
      setVoiceMode("recorded");
      setVoicePanel("recording");
    } else {
      setVoiceMode("automatic");
      setVoicePanel("automatic");
    }
    setVoiceGenerationState("idle");
    setVoiceSettingsStepId(stepId);
  };

  const saveVoiceSettings = async () => {
    let saved = false;
    if (voicePanel === "automatic" || voicePanel === "custom") {
      saved = await generateVoice();
    } else if (recordedBlob) {
      saved = await uploadVoiceRecording();
    } else {
      saved = voiceState.introduction?.mode === "recorded";
    }
    if (saved) setVoiceSettingsStepId(null);
  };

  const selectAudioFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    const duration = await new Promise((resolve) => {
      const audio = new Audio();
      audio.preload = "metadata";
      audio.onloadedmetadata = () => resolve(Number(audio.duration));
      audio.onerror = () => resolve(Number.NaN);
      audio.src = previewUrl;
    });
    if (!Number.isFinite(duration)) {
      URL.revokeObjectURL(previewUrl);
      event.target.value = "";
      setVoiceState((current) => ({ ...current, error: "לא ניתן לקרוא את קובץ הקול. בחרו קובץ שמע תקין." }));
      return;
    }
    if (duration > PERSONAL_RECORDING_LIMIT_SECONDS) {
      URL.revokeObjectURL(previewUrl);
      event.target.value = "";
      setVoiceState((current) => ({ ...current, error: "קובץ הקול ארוך מ־20 שניות. יש לבחור קובץ באורך של עד 20 שניות." }));
      return;
    }
    setVoiceState((current) => ({ ...current, error: "" }));
    setRecordedBlob(file);
    setRecordedFileName(file.name || "uploaded-audio");
    setRecordedSource("upload");
    setRecordedUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return previewUrl;
    });
  };

  const activeStepViews = new Map(draftSteps.map((step) => {
    const executionStatus = step.executionStatus || step.status;
    const completed = executionStatus === "completed";
    const failed = executionStatus === "failed";
    const voiceFailed = step.kind === "voice" && failed;
    const paused = executionStatus === "paused";
    const requiresAttention = executionStatus === "requiresAttention";
    const inProgress = ["claimed", "executing"].includes(executionStatus);
    const voiceProviderAttempted = step.kind === "voice" && Boolean(step.providerAttemptStartedAt
      || Object.keys(step.execution?.operations || {}).length > 0);
    const pastDue = !completed && !inProgress && !voiceFailed && !voiceProviderAttempted && new Date(step.scheduledAt).getTime() <= Date.now();
    const invalid = invalidStepIds.includes(step.id);
    // A started round may already have reached guests; rescheduling it would resend the whole round.
    const editable = !completed && !isStartedStep(step);
    const editing = editingStepId === step.id;
    const completionSummary = completed || voiceFailed ? completedStepSummary(step) : [];
    const attentionHelp = ["MESSAGE_CONFIRMATION_MISSING", "MESSAGE_RECOVERY_EXHAUSTED"].includes(step.requiresAttentionReason)
      ? "חלק מההודעות בסבב זה יצאו לשליחה, אך לא התקבל עליהן אישור. אין צורך לקבוע מועד חדש – הסבב לא יישלח שוב, והמקרה הועבר לבדיקה."
      : step.requiresAttentionReason === "PROVIDER_OUTCOME_UNKNOWN"
      ? "תוצאת הספק אינה ידועה. יש לבדוק את היסטוריית השליחה או החיוג לפני קביעת מועד חדש, כדי למנוע ביצוע כפול."
      : step.kind === "voice"
        ? "כרגע החיוג לא יתבצע — יש לשנות את המועד לתאריך ושעה עתידיים."
        : "כרגע ההודעה לא תישלח — יש לתקן את ההגדרה ולבחור מועד עתידי במידת הצורך.";
    const [stepDate = "", stepTime = "19:30"] = formatJerusalemDateTimeLocal(step.scheduledAt).split("T");
    return [step.id, {
      completed, voiceFailed, paused, requiresAttention, pastDue, invalid, editable, editing,
      completionSummary, attentionHelp, stepDate, stepTime,
    }];
  }));
  const editingStepIndex = draftSteps.findIndex((step) => step.id === editingStepId);
  const editingStep = editingStepIndex >= 0 ? draftSteps[editingStepIndex] : null;
  const editingStepView = editingStep ? activeStepViews.get(editingStep.id) : null;

  if (!visible) return null;

  return (
    <>
      {pauseCandidate && (
        <div className="autopilot-activation" role="presentation">
          <section role="dialog" aria-modal="true" aria-labelledby="autopilot-pause-title">
            <h2 id="autopilot-pause-title">השהיית שלב</h2>
            <p>{pauseCandidate.kind === "voice"
              ? "אם תשהה את השלב, החיוג לא יתבצע במועד שנקבע."
              : "אם תשהה את השלב, ההודעה לא תישלח במועד שנקבע."}</p>
            <div className="autopilot-activation__actions">
              <button type="button" onClick={() => {
                updateStep(pauseCandidate.id, { executionStatus: "paused" });
                setPauseCandidate(null);
              }}>מאשר להשהות</button>
              <button type="button" className="secondary" onClick={() => setPauseCandidate(null)}>ביטול</button>
            </div>
          </section>
        </div>
      )}

      {editingStep && editingStepView?.editable && (
        <div
          className={`autopilot-step-edit-modal__backdrop ${closingStepId === editingStep.id ? "is-closing" : "is-opening"}`}
          role="presentation"
          onMouseDown={(event) => { if (event.target === event.currentTarget) cancelStepEditor(); }}
        >
          <section className={`autopilot-step-edit-modal ${closingStepId === editingStep.id ? "is-closing" : "is-opening"}`} role="dialog" aria-modal="true" aria-labelledby="autopilot-step-edit-title" dir="rtl">
            <button type="button" className="autopilot-step-edit-modal__close" aria-label="סגירת עריכת השלב" onClick={cancelStepEditor}>×</button>
            <header className="autopilot-step-edit-modal__header">
              <span>שלב {String(editingStepIndex + 1).padStart(2, "0")}</span>
              <h2 id="autopilot-step-edit-title">עריכת מועד השליחה</h2>
            </header>
            <div className="autopilot-step__editor">
              <div className="invite-editor__event-details-row">
                <label htmlFor={`autopilot-step-date-${editingStepIndex}`} className="invite-editor__event-date-field">
                  <span>תאריך</span>
                  <EventDatePicker
                    id={`autopilot-step-date-${editingStepIndex}`}
                    value={editingStepView.stepDate}
                    onChange={(date) => {
                      try { updateStep(editingStep.id, { scheduledAt: parseJerusalemDateTimeLocal(`${date}T${editingStepView.stepTime}`), executionStatus: editingStepView.paused ? "paused" : "pending", requiresAttentionAt: null, requiresAttentionReason: null }); } catch (_error) { updateStep(editingStep.id, { scheduledAt: "" }); }
                    }}
                  />
                </label>
                <div className="invite-editor__event-time-field">
                  <span>שעה</span>
                  <EventTimePicker
                    value={editingStepView.stepTime}
                    onChange={(time) => {
                      try { updateStep(editingStep.id, { scheduledAt: parseJerusalemDateTimeLocal(`${editingStepView.stepDate}T${time}`), executionStatus: editingStepView.paused ? "paused" : "pending", requiresAttentionAt: null, requiresAttentionReason: null }); } catch (_error) { updateStep(editingStep.id, { scheduledAt: "" }); }
                    }}
                  />
                  <small>בחירה בפורמט 24 שעות.</small>
                </div>
              </div>
              {["message", "giftLink"].includes(editingStep.kind) && <label>ערוץ שליחה<select value={editingStep.channel} onChange={(event) => updateStep(editingStep.id, { channel: event.target.value, ...(editingStepView.requiresAttention && !editingStepView.pastDue ? { executionStatus: "pending", requiresAttentionAt: null, requiresAttentionReason: null } : {}) })}>
                <option value="whatsapp">WhatsApp</option><option value="sms">SMS</option>
              </select></label>}
              {editingStep.kind === "giftLink" && <label>קהל יעד<select value={editingStep.audience || "all"} onChange={(event) => updateStep(editingStep.id, { audience: event.target.value })}>
                <option value="all">כולם</option>
                <option value="approvedAndPending">אישרו הגעה + לא ענו</option>
                <option value="approved">רק אישרו הגעה</option>
              </select></label>}
              <div className="autopilot-step__editor-actions">
                {editingStepView.paused && <button type="button" onClick={() => reactivateStep(editingStep)}>הפעלת שלב</button>}
                {!editingStepView.paused && !editingStepView.pastDue && <button type="button" className="secondary" onClick={() => setPauseCandidate(editingStep)}>השהיית שלב</button>}
              </div>
            </div>
            <footer className="autopilot-step-edit-modal__footer">
              <button type="button" className="autopilot-save-button" disabled={busy} onClick={async () => {
                if (lifecycle.status === "inactive") {
                  toggleStepEditor(editingStep.id);
                  return;
                }
                if (await saveTimeline()) toggleStepEditor(editingStep.id);
              }}>שמירה</button>
              <button type="button" className="secondary" disabled={busy} onClick={cancelStepEditor}>ביטול</button>
            </footer>
          </section>
        </div>
      )}

      {visible && (
        <main className="autopilot-setup" dir="rtl">
          {lifecycle.status === "active" && <ActiveAutopilotBanner steps={draftSteps} />}
          {lifecycle.status !== "active" && (
            <header className="autopilot-setup__header">
              <div><span>Autopilot</span><h1>{assignedPackageHasVoice ? "לוח הודעות ושיחות לאירוע" : "לוח הודעות לאירוע"}</h1></div>
              {lifecycle.status === "stopped" && lifecycle.stoppedReason === "allRoundsCompleted"
                ? <strong className="autopilot-status">הסתיים</strong>
                : <strong className={`autopilot-status autopilot-status--${lifecycle.status}`}>{lifecycle.status}</strong>}
            </header>
          )}

          {loading && <p>טוען את הגדרות הטייס האוטומטי…</p>}
          {!loading && !assignment && <CommercePackagePicker eventId={docID} disabled={isReadOnly} freeLimitReached={isFreeContactDeletionLocked(eventData || {}, contacts)} />}
          {!loading && assignment && (
            <div className="autopilot-sections">
              <section className="autopilot-card autopilot-capacity">
                <span>שימוש ברשומות / מכסה</span>
                <strong>{usage.used} / {usage.limit || "—"} רשומות{usage.overage > 0 ? ` / חריגה ${usage.overage}` : ""}</strong>
              </section>

              {usage.overLimit && (
                <div className="autopilot-warning" role="alert">
                  <strong>חריגה ממכסת הרשומות</strong>
                  <small>{recordOverageMessage(usage)}</small>
                </div>
              )}

              {(activationBlocked || giftSchedulingBlocked || currentScheduleWarnings.length > 0) && (
                <PurchasedScheduleUnavailableWarning blocking={activationBlocked || giftSchedulingBlocked} gift={giftSchedulingBlocked && !activationBlocked} />
              )}

              {lifecycle.status === "inactive" && !activationBlocked && (
                <div className="autopilot-warning autopilot-warning--inactive">
                  <strong>{INACTIVE_WARNING}</strong>
                  {contacts.length === 0 && <small>יש להוסיף לפחות איש קשר אחד לפני ההפעלה.</small>}
                </div>
              )}

              {lifecycle.status === "inactive" && !activationBlocked && activationPreviewLoading && <section className="autopilot-card"><strong>מכין את לוח הזמנים המוצע…</strong></section>}
              {lifecycle.status === "inactive" && (hasOutdatedDraftTimes || activationPreviewError) && <p className="autopilot-error" role="alert">{hasOutdatedDraftTimes ? OUTDATED_DRAFT_MESSAGE : activationPreviewError}</p>}

              {!activationBlocked && assignedPackageHasVoice && (lifecycle.status === "inactive" || voiceSettingsStepId) && (
                <div
                  className={lifecycle.status === "inactive" ? "autopilot-voice-inline" : "autopilot-voice-modal__backdrop"}
                  role={lifecycle.status === "inactive" ? undefined : "presentation"}
                  onMouseDown={lifecycle.status === "inactive" ? undefined : (event) => {
                    if (event.target === event.currentTarget) setVoiceSettingsStepId(null);
                  }}
                >
                <section
                  ref={voiceSettingsRef}
                  tabIndex={lifecycle.status === "inactive" ? -1 : undefined}
                  className={lifecycle.status === "inactive" ? "autopilot-card autopilot-voice autopilot-voice--accordion" : "autopilot-step-edit-modal autopilot-voice autopilot-voice-modal"}
                  role={lifecycle.status === "inactive" ? undefined : "dialog"}
                  aria-modal={lifecycle.status === "inactive" ? undefined : "true"}
                  aria-labelledby="autopilot-voice-settings-title"
                >
                {lifecycle.status !== "inactive" && <button type="button" className="autopilot-step-edit-modal__close" aria-label="סגירת הגדרות קול" onClick={() => setVoiceSettingsStepId(null)}>×</button>}
                {lifecycle.status === "inactive" ? (
                  <button
                    type="button"
                    className="autopilot-voice__accordion-toggle"
                    aria-expanded={voiceSectionOpen}
                    aria-controls="autopilot-voice-settings-content"
                    onClick={() => setVoiceSectionOpen((current) => !current)}
                  >
                    <span className="autopilot-voice__accordion-icon" aria-hidden="true">♪</span>
                    <h2 id="autopilot-voice-settings-title">פתיח קולי לחייגן הטלפוני</h2>
                    <span className={`autopilot-voice__accordion-arrow${voiceSectionOpen ? " is-open" : ""}`} aria-hidden="true">▼</span>
                  </button>
                ) : (
                  <div className="autopilot-card__title"><div><h2 id="autopilot-voice-settings-title">{`הגדרות פתיח קולי — שלב ${draftSteps.findIndex((step) => step.id === voiceSettingsStepId) + 1}`}</h2></div></div>
                )}
                {(lifecycle.status !== "inactive" || voiceSectionOpen) && (
                <div id="autopilot-voice-settings-content" className={lifecycle.status === "inactive" ? "autopilot-voice__accordion-content" : undefined}>
                <div className="autopilot-voice__tabs" role="tablist" aria-label="בחירת סוג פתיח קולי">
                  <button type="button" disabled={recording} className={voicePanel === "automatic" ? "is-active" : ""} onClick={() => { setVoicePanel("automatic"); setVoiceMode("automatic"); setVoiceGenerationState("idle"); }}>פתיח אוטומטי</button>
                  <button type="button" disabled={recording} className={voicePanel === "custom" ? "is-active" : ""} onClick={() => { setVoicePanel("custom"); setVoiceMode("custom"); setVoiceGenerationState("idle"); }}>טקסט מותאם אישית</button>
                  <button type="button" disabled={recording} className={voicePanel === "recording" ? "is-active" : ""} onClick={() => setVoicePanel("recording")}>הקלטה אישית</button>
                  <button type="button" disabled={recording} className={voicePanel === "upload" ? "is-active" : ""} onClick={() => setVoicePanel("upload")}>העלאת קובץ קול</button>
                </div>

                {(voicePanel === "automatic" || voicePanel === "custom") && (
                  <div className="autopilot-voice__workspace">
                    {voicePanel === "custom" && (
                      <div className="autopilot-voice__custom-text">
                        <textarea
                          value={customText}
                          maxLength={CUSTOM_VOICE_TEXT_LIMIT}
                          onChange={(event) => setCustomText(event.target.value)}
                          placeholder={automaticVoiceText || "הקלידו את כל טקסט הפתיח שיישלח ל־Voice"}
                        />
                        <span>{customText.length}/{CUSTOM_VOICE_TEXT_LIMIT} תווים</span>
                      </div>
                    )}
                    {displayedVoiceText && (
                      <div className="autopilot-voice__text-preview">
                        <strong>{voiceState.introduction?.mode === voiceMode ? "הפתיח שיוקרא בתחילת שיחת הטלפון" : "תצוגת הפתיח לשיחת הטלפון"}</strong>
                        <div className="autopilot-voice__sent-text"><p>{displayedVoiceText}</p></div>
                      </div>
                    )}
                    <CompactAudioPlayer
                      src={displayedAudio}
                      disabled={voiceState.loading}
                      allowSeeking={!automaticVoiceApprovalRequired}
                      onEnded={() => {
                        if (voiceState.introduction?.mode === "automatic" && currentVoiceFingerprint) {
                          setListenedVoiceFingerprint(currentVoiceFingerprint);
                        }
                      }}
                    />
                    {automaticVoiceApprovalRequired && voicePanel === "automatic" && (
                      <div className="autopilot-voice__approval" role="alert">
                        <strong>יש לאשר את הפתיח לפני הפעלת הטייס האוטומטי.</strong>
                        <span>יש להאזין לפתיח עד סופו ולאחר מכן לאשר אותו.</span>
                        <button
                          type="button"
                          disabled={busy || listenedVoiceFingerprint !== currentVoiceFingerprint}
                          onClick={approveAutomaticVoice}
                        >
                          אישור הפתיח
                        </button>
                      </div>
                    )}
                    {voiceGenerationState === "success" && (
                      <p className="autopilot-voice__success" role="status">הפתיח נוצר בהצלחה. ניתן לשמוע אותו בלחיצה על ‘השמע פתיח לטלפון’.</p>
                    )}
                    <button type="button" className={voiceState.loading ? "autopilot-voice__generate-button is-loading" : "autopilot-voice__generate-button"} disabled={busy || voiceState.loading || (voiceMode === "custom" && !customText.trim())} onClick={generateVoice}>
                      {voiceState.loading && <span className="autopilot-voice__button-spinner" aria-hidden="true" />}
                      {voiceState.loading ? "מייצר פתיח" : "שמירה ויצירת פתיח"}
                    </button>
                  </div>
                )}

                {voicePanel === "recording" && (
                  <div className="autopilot-voice__capture">
                    <div className={`autopilot-voice__recording-status ${recording ? "is-recording" : ""}`}>
                      <strong>{recording ? "מקליט כעת" : recordedSource === "recording" ? "ההקלטה מוכנה לתצוגה" : "הקלטת פתיח אישי"}</strong>
                      <span>עד 20 שניות</span>
                    </div>
                    <div className="autopilot-voice__timer">00:{String(recordingSeconds).padStart(2, "0")} / 00:20</div>
                    <canvas ref={recordingCanvasRef} width="720" height="110" aria-label="תצוגת גל הקול בזמן ההקלטה" />
                    <div className="autopilot-voice__capture-actions">
                      {!recording ? <button type="button" onClick={startVoiceRecording}>התחלת הקלטה</button> : <button type="button" className="danger" onClick={stopVoiceRecording}>סיום הקלטה</button>}
                      {recordedSource === "recording" && recordedBlob && !recording && <button type="button" disabled={voiceState.loading} onClick={uploadVoiceRecording}>שמירת ההקלטה</button>}
                    </div>
                    <CompactAudioPlayer src={displayedAudio} />
                  </div>
                )}

                {voicePanel === "upload" && (
                  <div className="autopilot-voice__upload">
                    <label>
                      <strong>בחירת קובץ קול</strong>
                      <span>MP3, WAV, M4A, AAC, OGG או WebM · עד 20 שניות</span>
                      <input type="file" accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.webm" onChange={selectAudioFile} />
                    </label>
                    {recordedSource === "upload" && recordedBlob && (
                      <div className="autopilot-voice__file-preview">
                        <strong>{recordedFileName}</strong>
                        <CompactAudioPlayer src={displayedAudio} />
                        <button type="button" disabled={voiceState.loading} onClick={uploadVoiceRecording}>שמירת קובץ הקול</button>
                      </div>
                    )}
                  </div>
                )}
                <div className="autopilot-voice__test-call">
                  <div>
                    <strong>חיוג ניסיון</strong>
                    <span>השיחה תשמיע את הפתיח והמערכת הפעילים באירוע זה.</span>
                  </div>
                  <div className="autopilot-voice__test-call-controls">
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      dir="ltr"
                      value={testPhone}
                      placeholder="0501234567"
                      aria-label="מספר טלפון לחיוג ניסיון"
                      onChange={(event) => setTestPhone(event.target.value)}
                    />
                    <button type="button" disabled={testCallState.loading || !testPhone.trim()} onClick={startEventTestCall}>
                      {testCallState.loading ? "מחייג…" : "חייג"}
                    </button>
                  </div>
                  {testCallState.message && <p className="autopilot-voice__success" role="status">{testCallState.message}</p>}
                  {testCallState.error && <p className="autopilot-error" role="alert">{testCallState.error}</p>}
                </div>
                {voiceState.error && <p className="autopilot-error">{voiceState.error}</p>}
                {lifecycle.status !== "inactive" && (
                  <footer className="autopilot-step-edit-modal__footer autopilot-voice-modal__footer">
                    <button type="button" className="autopilot-save-button" disabled={busy || voiceState.loading || recording} onClick={saveVoiceSettings}>שמירה</button>
                    <button type="button" className="secondary" disabled={busy || voiceState.loading || recording} onClick={() => setVoiceSettingsStepId(null)}>ביטול</button>
                  </footer>
                )}
                </div>
                )}
              </section>
              </div>)}

              {displayPlan && (
                <section className="autopilot-card autopilot-timeline-card">
                  {scheduleWarnings.length > 0 && (
                    <div className="autopilot-warning" role="status">
                      ישנם שלבים המתוזמנים בהפרש קצר מהמרווח המומלץ.
                    </div>
                  )}
                  <PackageWorkflowCards
                    steps={draftSteps}
                    headingId="autopilot-active-workflow-title"
                    headingNote="ניתן לשנות שעות ותאריכים של השליחה בלחיצה על הקוביה"
                    className="autopilot-commerce__modal-workflow--active"
                    getCardClassName={(step) => {
                      const view = activeStepViews.get(step.id);
                      return `autopilot-commerce__workflow-card--active ${step.kind === "voice" ? "is-voice" : ""} ${view.editable ? "is-editable" : ""} ${view.completed ? "is-completed" : ""} ${view.voiceFailed ? "is-failed" : ""} ${view.paused ? "is-paused" : ""} ${view.pastDue || view.requiresAttention || view.invalid ? "requires-attention" : ""}`;
                    }}
                    renderCopyExtras={(step) => {
                      const view = activeStepViews.get(step.id);
                      return <>
                        {!view.completed && step.messagePurpose === "confirmedReminder" && hasTableNumberData && <small>התזכורת כוללת מספר שולחן במידה והוקצה שולחן לאורח</small>}
                        {(view.completed || view.voiceFailed) && <div className="autopilot-step__completion-summary">{view.completionSummary.map((item) => <span key={item}>{item}</span>)}</div>}
                        {(view.pastDue || view.requiresAttention) && <><em className="autopilot-step__state">דורש טיפול</em><small>{view.attentionHelp}</small></>}
                        {(view.paused || view.voiceFailed || view.pastDue || view.requiresAttention) && <div className="autopilot-step__summary-status"><span>{view.paused ? "מושהה" : view.voiceFailed ? "לא הושלם" : "דורש טיפול"}</span></div>}
                      </>;
                    }}
                    renderCardAction={(step, index) => {
                      const view = activeStepViews.get(step.id);
                      if (step.kind === "voice" && lifecycle.status !== "inactive") {
                        return <span className="autopilot-step__voice-actions">
                          <VoiceStagePreviewButton
                            src={voiceState.introduction?.playbackUrl || ""}
                            disabled={voiceState.loading}
                          />
                          <button type="button" onClick={(event) => { event.stopPropagation(); openVoiceSettings(step.id); }}>הגדרות</button>
                        </span>;
                      }
                      return view.editable ? <span className="autopilot-step__gear-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M19.4 13a7.5 7.5 0 0 0 .05-1 7.5 7.5 0 0 0-.05-1l2.1-1.65-2-3.46-2.48 1a7.7 7.7 0 0 0-1.73-1L14.9 3h-4l-.39 2.89a7.7 7.7 0 0 0-1.73 1l-2.48-1-2 3.46L6.4 11a7.5 7.5 0 0 0-.05 1 7.5 7.5 0 0 0 .05 1l-2.1 1.65 2 3.46 2.48-1a7.7 7.7 0 0 0 1.73 1L10.9 21h4l.39-2.89a7.7 7.7 0 0 0 1.73-1l2.48 1 2-3.46L19.4 13ZM12.9 15.2a3.2 3.2 0 1 1 0-6.4 3.2 3.2 0 0 1 0 6.4Z" /></svg></span> : null;
                    }}
                    isCardClickable={(step) => activeStepViews.get(step.id).editable}
                    getCardAriaLabel={(_step, index) => `עריכת שלב ${index + 1}`}
                    onCardClick={(step) => toggleStepEditor(step.id)}
                  />
                  {lifecycle.status !== "inactive" && (
                    <button type="button" disabled={busy || isReadOnly} onClick={saveTimeline}>שמירת שינויי לוח הזמנים</button>
                  )}
                </section>
              )}

              {lifecycle.status === "inactive" && !activationBlocked && displayPlan && (
                <div className="autopilot-final-action">
                  <button type="button" disabled={busy || isReadOnly || contacts.length === 0 || usage.overLimit || !activationPreview || activationBlocked} onClick={activate}>
                    שמירת לוחות הזמנים והפעלת הטייס
                  </button>
                </div>
              )}

            </div>
          )}
        </main>
      )}
    </>
  );
};

export default AutopilotSetup;
